import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import HeaderType01 from '../SiteSubGenerator/HeaderType01';
import HeaderType02 from '../SiteSubGenerator/HeaderType02';
import { useBuilder } from '../../contexts/BuilderContext';

const HeaderWrapper = styled.div`
  padding: 24px 20px 20px;
  background-color: ${(props) => props.backgroundColor || 'transparent'};

  /* Break out of container to fill full viewport width */
  ${(props) => props.backgroundColor && props.backgroundColor !== 'transparent' ? `
    position: relative;
    width: 100vw;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
  ` : ''}

  @media (max-width: 768px) {
    padding: 16px 15px 12px;
  }
`;

const HEADER_COMPONENTS = {
  'siteSub.header-01': HeaderType01,
  'siteSub.header-02': HeaderType02,
};

const GlobalHeader = ({ site: propSite, headerType }) => {
  // 실시간 미리보기를 위해 liveSite 사용 (없으면 props의 site 사용)
  const { liveSite } = useBuilder();
  const site = liveSite || propSite;

  if (!site || !headerType) return null;

  const HeaderComponent = HEADER_COMPONENTS[headerType];
  if (!HeaderComponent) return null;

  // HeaderType01 props
  if (headerType === 'siteSub.header-01') {
    return (
      <HeaderWrapper backgroundColor={site.headerBackgroundColor || 'transparent'}>
        <HeaderComponent
          siteId={site.id}
          logo={site.logo}
          title={site.title || site.eName || ''}
          subtitle={site.subtitle || ''}
          blogEnabled={site.blogSettings?.enabled || false}
          sns={{
            facebook: site.facebook || '',
            instagram: site.instagram || '',
            youtube: site.youtube || '',
          }}
          titleMode={site.headerTitleMode || 'text'}
          titleImage={site.headerTitleImage || ''}
          // 회사 로고 관련 props
          companyLogo={site.headerCompanyLogo || ''}
          companyLogoLink={site.headerCompanyLogoLink || ''}
          companyLogoPosition={site.headerCompanyLogoPosition || 'top'}
          companyLogoMargin={site.headerCompanyLogoMargin || '0 0 15px 0'}
          companyLogoWidth={site.headerCompanyLogoWidth || 'auto'}
          companyLogoHeight={site.headerCompanyLogoHeight || '50px'}
          // titleImage 스타일 옵션
          titleImageMargin={site.headerTitleImageMargin || '0 0 10px 0'}
          titleImageMaxHeight={site.headerTitleImageMaxHeight || '120px'}
          titleImageMaxWidth={site.headerTitleImageMaxWidth || 'auto'}
          // 헤더 배경색
          backgroundColor={site.headerBackgroundColor || 'transparent'}
          // SNS 링크 색상
          snsLinkBackgroundColor={site.snsLinkBackgroundColor || '#ffffff'}
          snsLinkTextColor={site.snsLinkTextColor || '#6f7075'}
        />
      </HeaderWrapper>
    );
  }

  // HeaderType02 props
  if (headerType === 'siteSub.header-02') {
    return (
      <HeaderWrapper backgroundColor={site.headerBackgroundColor || 'transparent'}>
        <HeaderComponent
          id="global-header"
          containerClass="text-center"
          options={{ companylogo: site.logo || '' }}
          blogEnabled={site.blogSettings?.enabled || false}
          sns={{
            facebook: site.facebook || '',
            instagram: site.instagram || '',
            youtube: site.youtube || '',
            blog: site.blog || '',
          }}
          brokerage={site.brokerage}
        />
      </HeaderWrapper>
    );
  }

  return null;
};

GlobalHeader.propTypes = {
  site: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    logo: PropTypes.string,
    title: PropTypes.string,
    eName: PropTypes.string,
    subtitle: PropTypes.string,
    facebook: PropTypes.string,
    instagram: PropTypes.string,
    youtube: PropTypes.string,
    blog: PropTypes.string,
    blogSettings: PropTypes.shape({
      enabled: PropTypes.bool,
    }),
    brokerage: PropTypes.object,
    headerTitleMode: PropTypes.oneOf(['text', 'image']),
    headerTitleImage: PropTypes.string,
    // 회사 로고 관련
    headerCompanyLogo: PropTypes.string,
    headerCompanyLogoLink: PropTypes.string,
    headerCompanyLogoPosition: PropTypes.oneOf(['top', 'left', 'bottom']),
    headerCompanyLogoMargin: PropTypes.string,
    headerCompanyLogoWidth: PropTypes.string,
    headerCompanyLogoHeight: PropTypes.string,
    // titleImage 스타일 옵션
    headerTitleImageMargin: PropTypes.string,
    headerTitleImageMaxHeight: PropTypes.string,
    headerTitleImageMaxWidth: PropTypes.string,
    // 헤더 배경색
    headerBackgroundColor: PropTypes.string,
    // SNS 링크 색상
    snsLinkBackgroundColor: PropTypes.string,
    snsLinkTextColor: PropTypes.string,
  }),
  headerType: PropTypes.string,
};

GlobalHeader.defaultProps = {
  site: null,
  headerType: 'siteSub.header-01',
};

export default GlobalHeader;
